import { Component, OnInit } from '@angular/core';
import { Item } from 'devextreme/ui/tree_view';
import CustomStore from 'devextreme/data/custom_store';
import { first } from 'rxjs/operators';
import { IncidentsService } from './services/incidents.service';
import { ReportsService } from './services/reports.service';
import { HelperService } from './services/helper.service';
import { AuthorizationService } from './services/authorization.service';
import { ApiCommunicationService } from './services/api-communication.service';
import { TranslateService } from '@ngx-translate/core';
import { _ } from '@biesbjerg/ngx-translate-extract/dist/utils/utils';
import { LoadPanelService } from './services/load-panel.service';
import { locale } from 'devextreme/localization';
import { NotificationService } from './services/notification.service';
import { ApplicationInformationItem } from './model/application-information-item.model';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {

  isAuthorized = false;
  userName;
  isAuthorizationInProgress = false;
  divisionsDataSource: CustomStore;
  selectedOpenMode = 'push';
  selectedPosition = 'left';
  selectedRevealMode = 'expand';
  isDrawerOpen = true;
  shadingColor = HelperService.getLoadPanelShadeColor();
  isLoadPanelVisible = false;
  toolbarContent = [{
    widget: 'dxButton',
    location: 'before',
    options: {
      icon: 'menu',
      onClick: () => this.isDrawerOpen = !this.isDrawerOpen,
    },
  }];
  isLogoutMenuVisible = false;
  applicationInformation: ApplicationInformationItem[];
  isAboutDialogVisible = false;
  incidentManagementEnabled = false;

  private readonly INCIDENTS_ID_PREFIX = 'incidents';
  private readonly REPORTS_ID_PREFIX = 'reports';

  constructor(private incidentsService: IncidentsService,
              private reportsService: ReportsService,
              private helperService: HelperService,
              private apiCommunicationService: ApiCommunicationService,
              private authorizationService: AuthorizationService,
              private translateService: TranslateService,
              private loadPanelService: LoadPanelService,
              private notificationService: NotificationService) {

    locale(this.helperService.getStartupConfiguration().defaultLanguage);
  }

  ngOnInit(): void {
    this.incidentManagementEnabled = this.helperService.getStartupConfiguration().incidentManagementEnabled;
    this.readEntryParameters();
    this.getLoadPanelVisibility();
    this.getIsAuthorizationInProgressNotification();
    this.getAuthorizationResult();
  }

  treeViewItemClicked(item: any): void {
    if (item.node?.key && item.node?.parent?.key) {
      if (item.node.key.toString().startsWith(this.INCIDENTS_ID_PREFIX)) {
        this.incidentsService.createIncidentNotify(item.node.parent.key);
      } else if (item.node.key.toString().startsWith(this.REPORTS_ID_PREFIX)) {
        this.reportsService.createReportNotify(item.node.parent.key);
      }
    }
  }

  getDrawerHeight(): string {
    return HelperService.getContentHeight();
  }

  showLogoutOptions(): void {
    this.isLogoutMenuVisible = true;
  }

  logout(): void {
    this.isLogoutMenuVisible = false;
    this.authorizationService.logout().pipe(first()).subscribe(result => {
      if (result.error) {
        if (result.error?.error?.error_description) {
          this.notificationService.showErrorMessage(result.error.error.error_description);
        }
      }
    });
  }

  about(): void {
    this.isLogoutMenuVisible = false;
    this.apiCommunicationService.getApplicationInformation().pipe(first()).subscribe(applicationInformation => {
      this.applicationInformation = applicationInformation;
      this.isAboutDialogVisible = true;
    });
  }

  private getIsAuthorizationInProgressNotification(): void {
    this.authorizationService.isAuthorizationInProgress().subscribe(isAuthorizationInProgress => {
      this.isAuthorizationInProgress = isAuthorizationInProgress;
    });
  }

  private getAuthorizationResult(): void {
    this.authorizationService.isAuthorized().subscribe(result => {
      if (result.isAuthorized) {
        this.setDivisionTreeViewItems();
        this.userName = this.authorizationService.getUser().Name;
      }
      this.isAuthorized = result.isAuthorized;
    });
  }

  private readEntryParameters(): void {
    this.helperService.readEntryParameters();
  }

  private setDivisionTreeViewItems(): void {
    this.apiCommunicationService.getDivisions().pipe(first()).subscribe(divisionsContainer => {
      const allDivisions: Item[] = [];
      divisionsContainer._Divisions.forEach(item => {
        const menuItem = {
          id: item.ID,
          text: item.Name,
          expanded: true,
          icon: `../assets/images/main-menu/Div_${item.ID}.svg`,
          items: [
            {
              id: `${this.REPORTS_ID_PREFIX}_${item.ID}_1`,
              text: this.translateService.instant(_('New Report')),
              icon: `../assets/images/main-menu/1_${item.ID}.svg`,
              expanded: true
            }
          ]
        };
        const newIncidentMenuItem = {
          id: `${this.INCIDENTS_ID_PREFIX}_${item.ID}_2`,
          text: this.translateService.instant(_('New Incident')),
          icon: `../assets/images/main-menu/2_${item.ID}.svg`,
          expanded: true
        };
        if (this.incidentManagementEnabled) {
          menuItem.items.push(newIncidentMenuItem);
        }
        allDivisions.push(menuItem);
      });
      this.divisionsDataSource = new CustomStore({
        key: 'ID',
        loadMode: 'raw', // omit in the DataGrid, TreeList, PivotGrid, and Scheduler
        load: () => {
          return allDivisions;
        }
      });
    });
  }

  private getLoadPanelVisibility(): void {
    this.loadPanelService.getLoadPanelVisibility().subscribe(isVisible => {
      this.isLoadPanelVisible = isVisible;
    });
  }
}
